import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { usePrefersReducedMotion } from '@hooks';

const StyledPapersSection = styled.section`
  max-width: 1000px;

  .inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }

  .section-header {
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
      margin-bottom: 30px;
    }

    h2 {
      margin: 0;
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      margin-left: 30px;
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      color: var(--green);
      white-space: nowrap;
    }
  }

  .papers-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin: 80px auto 0;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  .more-button,
  .show-all-button {
    ${({ theme }) => theme.mixins.button};
  }
`;

const StyledPapersGrid = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  display: grid;
  gap: 25px;
`;

const StyledPaper = styled.li`
  ${({ theme }) => theme.mixins.boxShadow};
  display: flex;
  background: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 25px;
  transition: var(--smooth-transition);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--green), var(--blue));
    transform: translateX(-100%);
    transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }

  &:hover {
    transform: translateY(-5px);
    background: var(--light-navy);
    backdrop-filter: blur(10px);
    border-color: var(--white-new);

    &:before {
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }

  .paper-journal-cover {
    flex-shrink: 0;
    margin-right: 25px;
    width: 100px;
    height: 130px;
    border-radius: var(--border-radius);
    overflow: hidden;
    background: var(--navy);
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .placeholder {
      color: var(--slate);
      font-size: var(--fz-xs);
      text-align: center;
      padding: 10px;
    }

    @media (max-width: 768px) {
      margin-right: 0;
      margin-bottom: 20px;
      width: 80px;
      height: 100px;
      align-self: center;
    }
  }

  .paper-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .paper-overline {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    margin-bottom: 10px;
  }

  .paper-title {
    color: var(--lightest-slate);
    font-size: var(--fz-lg);
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: 10px;

    a {
      text-decoration: none;
      color: inherit;

      &:hover {
        color: var(--green);
      }
    }
  }

  .paper-authors {
    color: var(--light-slate);
    font-size: var(--fz-sm);
    margin-bottom: 8px;
    font-style: italic;
  }

  .paper-publication {
    color: var(--white-new);
    font-size: var(--fz-xs);
    margin-bottom: 15px;
    font-weight: 500;
  }

  .paper-abstract {
    color: var(--slate);
    font-size: var(--fz-sm);
    line-height: 1.4;
    margin-bottom: 15px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .paper-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 10px;
      align-items: flex-start;
    }
  }

  .paper-date {
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }

  .paper-links {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .paper-link {
    ${({ theme }) => theme.mixins.flexCenter};
    padding: 6px;
    color: var(--light-slate);
    transition: var(--transition);

    &:hover {
      color: var(--green);
      transform: translateY(-2px);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .doi-link {
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    text-decoration: none;
    padding: 2px 6px;
    border: 1px solid var(--light-slate);
    border-radius: 2px;
    transition: var(--transition);

    &:hover {
      color: var(--green);
      border-color: var(--green);
    }
  }
`;

const Papers = () => {
  const data = useStaticQuery(graphql`
    {
      papers: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/papers/" } }
        sort: { fields: [frontmatter___date], order: DESC }
        limit: 9
      ) {
        edges {
          node {
            fileAbsolutePath
            frontmatter {
              title
              authors
              date
              publication_type
              publication
              journal_cover
              abstract
              doi
              links {
                url
                pdf
                github
                linkedin
              }
            }
          }
        }
      }
    }
  `);

  const [showMore, setShowMore] = useState(false);
  const allPapers = data.papers.edges.filter(({ node }) => node);
  const INITIAL_LIMIT = 3;
  const firstThree = allPapers.slice(0, INITIAL_LIMIT);
  const papersToShow = showMore ? allPapers : firstThree;

  const revealTitle = useRef(null);
  const revealPapers = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealPapers.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <StyledPapersSection id="papers">
      <div className="inner">
        <div className="section-header">
          <h2 className="numbered-heading" ref={revealTitle}>
            Recent Publications
          </h2>
          <Link to="/publications">view all papers</Link>
        </div>
      </div>

      <StyledPapersGrid>
        {papersToShow &&
          papersToShow.map(({ node }, i) => {
            const { fileAbsolutePath } = node;
            const {
              title,
              authors,
              date,
              publication_type,
              publication,
              journal_cover,
              abstract,
              doi,
              links,
            } = node.frontmatter;

            // Get paper folder name from file path
            const pathParts = fileAbsolutePath.replace(/\\/g, '/').split('/');
            const paperFolder = pathParts[pathParts.length - 2]; // Get the folder name (second to last part)

            return (
              <StyledPaper key={i} ref={el => (revealPapers.current[i] = el)}>
                <div className="paper-journal-cover">
                  <img
                    src={
                      journal_cover
                        ? `/journal-covers/${journal_cover}`
                        : '/journal-covers/default-cover.png'
                    }
                    alt={publication}
                    onError={e => {
                      // Fallback to placeholder if journal cover doesn't exist
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = '<div class="placeholder">No Cover</div>';
                    }}
                  />
                </div>

                <div className="paper-content">
                  <div className="paper-overline">{publication_type}</div>

                  <h3 className="paper-title">
                    {links?.url ? (
                      <a href={links.url} target="_blank" rel="noopener noreferrer">
                        {title}
                      </a>
                    ) : (
                      title
                    )}
                  </h3>

                  <div className="paper-authors">
                    {authors && authors.length > 0
                      ? authors
                        .map(author => (author === 'admin' ? 'Mirza Waleed' : author))
                        .join(', ')
                      : 'No authors listed'}
                  </div>

                  <div className="paper-publication">{publication}</div>

                  <div className="paper-abstract">{abstract}</div>

                  <div className="paper-meta">
                    <div className="paper-date">
                      {new Date(date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                      })}
                    </div>

                    <div className="paper-links">
                      <a
                        href={`/papers/${paperFolder}/cite.bib`}
                        download={`${paperFolder}.bib`}
                        className="doi-link"
                        aria-label="Download Citation"
                        title="Download BibTeX Citation">
                        Cite
                      </a>
                      {doi && (
                        <a
                          href={`https://doi.org/${doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="doi-link">
                          DOI
                        </a>
                      )}
                      {links?.pdf && (
                        <a
                          href={`/${links.pdf}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="doi-link"
                          aria-label="View PDF">
                          PDF
                        </a>
                      )}
                      {links?.github && (
                        <a
                          href={links.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="doi-link"
                          aria-label="GitHub Repository">
                          GitHub
                        </a>
                      )}
                      {links?.linkedin && (
                        <a
                          href={links.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="doi-link"
                          aria-label="LinkedIn Post">
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </StyledPaper>
            );
          })}
      </StyledPapersGrid>

      {allPapers.length > INITIAL_LIMIT && (
        <div className="papers-buttons">
          <button className="more-button" onClick={() => setShowMore(!showMore)}>
            Show {showMore ? 'Less' : 'More'}
          </button>
          <Link to="/publications" className="show-all-button">
            Show All Papers
          </Link>
        </div>
      )}
    </StyledPapersSection>
  );
};

export default Papers;
